// src/utils/profilePhotoSelection.js
//
// Phase N11B: pure derivation of the final photos array and the
// canonical profileImage from a user's existing (kept) photos and any
// newly uploaded photos in this save. Fixes the N11A-proven defect in
// EditProfileForm.js where a newly uploaded photo was silently appended
// AFTER existing photos, leaving profileImage - the single field every
// consumer (Home, publicProfiles, chat, calls) reads - pointed at the
// old photo unless every prior photo happened to be deleted first.
//
// Pure - no Firebase/Storage/React/logging/side effects - directly
// unit-testable, and never mutates its inputs.

const DEFAULT_MAX_PHOTOS = 4;

function dedupeKeepFirst(items) {
  const seen = new Set();
  const out = [];
  for (const item of items) {
    if (!item || seen.has(item)) continue;
    seen.add(item);
    out.push(item);
  }
  return out;
}

/**
 * @param {{ keptExisting?: string[], uploadedPaths?: string[], maxPhotos?: number }} input
 * @return {{ finalPhotos: string[], profileImage: string|null }}
 */
export function selectFinalPhotos({ keptExisting, uploadedPaths, maxPhotos } = {}) {
  const kept = Array.isArray(keptExisting) ? keptExisting.filter(Boolean) : [];
  const uploaded = Array.isArray(uploadedPaths) ? uploadedPaths.filter(Boolean) : [];
  const max =
    Number.isFinite(maxPhotos) && maxPhotos > 0 ? maxPhotos : DEFAULT_MAX_PHOTOS;

  // Newly uploaded photos take the primary slot(s) - a photo the user
  // just added is what they mean by "my new photo" and must become
  // profileImage automatically. Previously-kept photos remain available
  // afterward as gallery photos, in their existing relative order.
  const ordered = uploaded.length > 0 ? [...uploaded, ...kept] : [...kept];

  // Uploaded photos are always first, so if the combined list somehow
  // exceeds maxPhotos, slicing only ever drops from the tail (old kept
  // photos) - a freshly uploaded photo's path is never silently
  // discarded from Firestore by this step.
  const finalPhotos = dedupeKeepFirst(ordered).slice(0, max);
  const profileImage = finalPhotos[0] || null;

  return { finalPhotos, profileImage };
}
